/**
 * Shared wizard layout utilities — WizardCanvas, FormSection, WIZ constants.
 * All creation steps import from here so spacing/layout is verbatim across them.
 */
import { type CSSProperties, type ReactNode, type RefObject } from "react";
import { Card, Icon, T, tx } from "@v0y4ge/jrni-ui";
import { useServerFieldErrors } from "./server-errors";

export const WIZ = { pad: "32px 24px", sectionGap: 24, stackGap: 16, cardGap: 16, cardPad: 24, cardPadTight: 16, railPad: 16 };

const WIZ_WIDTHS: Record<string, number> = { narrow: 560, mid: 760, wide: 1060 };

export function WizardCanvas({ width = "wide", cols, gap = WIZ.sectionGap, align = "start", children, style, containerRef }: {
  width?: string; cols?: "split" | "two" | "stack"; gap?: number; align?: string;
  children?: ReactNode; style?: CSSProperties; containerRef?: RefObject<HTMLDivElement | null>;
}) {
  const max = WIZ_WIDTHS[width] ?? null;
  if (cols === "split") {
    const kids = Array.isArray(children) ? children : [children];
    const main = kids[0]; const rail = kids.slice(1);
    return (
      <div ref={containerRef} style={{ width: "100%", maxWidth: max || "none", margin: "0 auto", padding: WIZ.pad, boxSizing: "content-box", display: "flex", flexWrap: "wrap", gap, alignItems: align, ...style }}>
        <div style={{ flex: "999 1 360px", minWidth: 0 }}>{main}</div>
        {rail.length ? <div style={{ flex: "1 1 300px", minWidth: 0 }}>{rail}</div> : null}
      </div>
    );
  }
  const grid = cols === "two" ? "repeat(auto-fit, minmax(320px, 1fr))" : null;
  const layout: CSSProperties = grid ? { display: "grid", gridTemplateColumns: grid, gap, alignItems: align } :
    cols === "stack" ? { display: "flex", flexDirection: "column", gap: WIZ.stackGap } : {};
  return (
    <div ref={containerRef} style={{ width: "100%", maxWidth: max || "none", margin: "0 auto", padding: WIZ.pad, boxSizing: "content-box", ...layout, ...style }}>
      {children}
    </div>
  );
}

/**
 * FieldError — THE shared inline field-error component (2026-07-12 docket §2).
 * Renders the server-returned `error.fields[]` message(s) whose path matches
 * `path` (prefix match), directly under the owning input. Driven entirely by
 * the ServerErrors store — it renders nothing until a write returns a 422 and
 * clears as the user edits the owning field. One component, every step; the
 * server's `message` is rendered verbatim (no client-invented copy).
 */
export function FieldError({ path, style }: { path: string | string[]; style?: CSSProperties }) {
  const errs = useServerFieldErrors(path);
  if (!errs.length) return null;
  return (
    <div role="alert" data-testid="create-field-error" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, ...style }}>
      {errs.map((f, i) => (
        <div key={`${f.path}-${f.code}-${i}`} data-path={f.path} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, lineHeight: 1.45, color: T.red1, fontWeight: 500 }}>
          <span style={{ flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.warn size={12} /></span>
          <span>{f.message}</span>
        </div>
      ))}
    </div>
  );
}

export function FormSection({ title, subtitle, children, style }: { title?: ReactNode; subtitle?: ReactNode; children?: ReactNode; style?: CSSProperties }) {
  return (
    <Card style={{ padding: WIZ.cardPad, ...style }}>
      {(title || subtitle) ? (
        <div style={{ marginBottom: 16 }}>
          {title ? <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{title}</h3> : null}
          {subtitle ? <p style={{ ...tx.help, margin: "4px 0 0" }}>{subtitle}</p> : null}
        </div>
      ) : null}
      {children}
    </Card>
  );
}
